import { formatCop } from "@/lib/credits";
import { formatBogotaLongDate } from "@/lib/subscription-end";

export const RECARGAS_SWITCH_FUNCTION = "billing-recargas-switch";
export const RECARGAS_SWITCH_ROLE = "admin";

export type RecargasSwitchPath = "immediate" | "scheduled";

export interface ScheduledRecargasSwitch {
  requestId: string;
  effectiveDate: string;
  releaseOn: string | null;
  released: boolean;
  cancellable: boolean;
}

export interface RecargasSwitchQuote {
  ok: boolean;
  reason: string | null;
  detail: string | null;
  message: string | null;
  path: RecargasSwitchPath | null;
  planName: string | null;
  priceCop: number | null;
  liveAuthorizations: number;
  sessions: number;
  carriedCop: number;
  owedAfterCop: number;
  amountOwed: number | null;
  effectiveDate: string | null;
  releaseOn: string | null;
  releaseNow: boolean;
  cancellableUntil: string | null;
  scheduled: ScheduledRecargasSwitch | null;
  quoteHash: string | null;
}

export type RecargasSwitchResult =
  | {
    path: "immediate";
    sessionsCarried: number;
    owedCop: number;
    authorizationsCancelled: number;
    priceCop: number | null;
  }
  | {
    path: "scheduled";
    requestId: string;
    effectiveDate: string;
    releaseOn: string | null;
    released: boolean;
    cancellableUntil: string | null;
    priceCop: number | null;
  };

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object";
const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const str = (value: unknown): string | null => (typeof value === "string" && value.length > 0 ? value : null);

export const CUSTOM_PLAN_DETAIL = "custom_plan";

export function isCustomPlanRefusal(quote: Pick<RecargasSwitchQuote, "ok" | "detail"> | null | undefined): boolean {
  return !!quote && !quote.ok && quote.detail === CUSTOM_PLAN_DETAIL;
}

export function recargasRequestMessage(orgName: string | null | undefined): string {
  return orgName
    ? `Hola, quiero pasar la cuenta ${orgName} a pagar con créditos.`
    : "Hola, quiero pasar mi cuenta a pagar con créditos.";
}

export function canSwitchToRecargas(role: string | null | undefined): boolean {
  return role === RECARGAS_SWITCH_ROLE;
}

export function parseRecargasQuote(raw: unknown): RecargasSwitchQuote | null {
  if (!isRecord(raw)) return null;
  const path = raw.path === "immediate" || raw.path === "scheduled" ? raw.path : null;
  const ok = raw.ok === true && path !== null;
  const s = isRecord(raw.scheduled) ? raw.scheduled : null;
  return {
    ok,
    reason: str(raw.reason),
    detail: str(raw.detail),
    message: str(raw.message),
    path: ok ? path : null,
    planName: str(raw.planName),
    priceCop: num(raw.priceCop),
    liveAuthorizations: num(raw.liveAuthorizations) ?? 0,
    sessions: num(raw.sessions) ?? 0,
    carriedCop: num(raw.carriedCop) ?? 0,
    owedAfterCop: num(raw.owedAfterCop) ?? 0,
    amountOwed: num(raw.amountOwed),
    effectiveDate: str(raw.effectiveDate),
    releaseOn: str(raw.releaseOn),
    releaseNow: raw.releaseNow === true,
    cancellableUntil: str(raw.cancellableUntil),
    scheduled: s && str(s.requestId) && str(s.effectiveDate)
      ? {
        requestId: String(s.requestId),
        effectiveDate: String(s.effectiveDate),
        releaseOn: str(s.releaseOn),
        released: s.released === true,
        cancellable: s.cancellable === true,
      }
      : null,
    quoteHash: ok ? str(raw.quoteHash) : null,
  };
}

export function parseRecargasSwitchResult(raw: unknown): RecargasSwitchResult | null {
  if (!isRecord(raw) || raw.ok !== true) return null;
  if (raw.path === "immediate") {
    return {
      path: "immediate",
      sessionsCarried: num(raw.sessions_carried) ?? 0,
      owedCop: num(raw.owed_cop) ?? 0,
      authorizationsCancelled: num(raw.authorizations_cancelled) ?? 0,
      priceCop: num(raw.price_cop),
    };
  }
  const requestId = str(raw.request_id);
  const effectiveDate = str(raw.effective_date);
  if (raw.path !== "scheduled" || !requestId || !effectiveDate) return null;
  return {
    path: "scheduled",
    requestId,
    effectiveDate,
    releaseOn: str(raw.release_on),
    released: raw.released === true,
    cancellableUntil: str(raw.cancellable_until),
    priceCop: num(raw.price_cop),
  };
}

export function dayBefore(date: string | null): string | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const at = new Date(`${date}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() - 1);
  return at.toISOString().slice(0, 10);
}

export function longDate(date: string | null): string {
  return date ? formatBogotaLongDate(date) : "";
}

function priceLine(priceCop: number | null): string | null {
  return priceCop !== null && priceCop > 0 ? `Cada conversación que atiende la IA cuesta ${formatCop(priceCop)} de tu saldo.` : null;
}

export function immediateQuoteLines(quote: RecargasSwitchQuote): string[] {
  const lines = ["Tu cuenta pasa a pagar con créditos ahora: sin mensualidad ni débito automático."];
  if (quote.carriedCop > 0) {
    const sessions = quote.sessions === 1 ? "1 conversación" : `${quote.sessions} conversaciones`;
    lines.push(`Lo que debes del plan por uso (${formatCop(quote.carriedCop)}, ${sessions}) queda como deuda en tu saldo de créditos.`);
  }
  if (quote.liveAuthorizations > 0) lines.push("Cancelamos el débito automático de tu tarjeta.");
  const price = priceLine(quote.priceCop);
  if (price) lines.push(price);
  lines.push(
    quote.owedAfterCop > 0
      ? "La IA vuelve a responder cuando tu saldo cubre la deuda y una conversación más."
      : "Recarga saldo para que la IA responda.",
  );
  return lines;
}

export function scheduledQuoteLines(quote: RecargasSwitchQuote): string[] {
  const date = longDate(quote.effectiveDate);
  const plan = quote.planName ? `Tu plan ${quote.planName}` : "Tu plan";
  const lines = [`${plan} sigue funcionando igual hasta el ${date}. Ese día tu cuenta pasa a pagar con créditos y no se cobra otro mes.`];
  if (quote.liveAuthorizations > 0) {
    lines.push(
      quote.releaseNow
        ? "Cancelamos hoy el débito automático de tu tarjeta para que no se cobre otro mes."
        : `El ${longDate(quote.releaseOn)} cancelamos el débito automático de tu tarjeta.`,
    );
  }
  lines.push(
    quote.cancellableUntil
      ? `Puedes cancelar el cambio hasta el ${longDate(quote.cancellableUntil)}.`
      : "Como faltan pocos días para el cierre del ciclo, el cambio no se podrá deshacer.",
  );
  lines.push(
    quote.priceCop !== null && quote.priceCop > 0
      ? `Desde el ${date}, cada conversación que atiende la IA cuesta ${formatCop(quote.priceCop)} de tu saldo. Recarga antes para que la IA siga respondiendo.`
      : `Recarga antes del ${date} para que la IA siga respondiendo.`,
  );
  return lines;
}

export function debtLine(amount: number | null): string {
  const owed = amount !== null && amount > 0 ? `${formatCop(amount)} pendientes` : "un pago pendiente";
  return `Tienes ${owed} de tu plan. Págalos primero y luego podrás pasar a pagar con créditos.`;
}

export function scheduledStatusLines(scheduled: ScheduledRecargasSwitch): string[] {
  const lines = [`Tu cuenta pasa a pagar con créditos el ${longDate(scheduled.effectiveDate)}. Hasta ese día tu plan sigue igual.`];
  const until = dayBefore(scheduled.releaseOn);
  if (scheduled.cancellable && until) lines.push(`Puedes cancelar el cambio hasta el ${longDate(until)}.`);
  else if (scheduled.cancellable) lines.push("Todavía puedes cancelar el cambio.");
  else lines.push("El débito automático ya se canceló, así que el cambio no se puede deshacer.");
  return lines;
}

export function recargasCardCopy(quote: RecargasSwitchQuote): { text: string; button: string; scheduled: boolean; whatsapp?: boolean } | null {
  if (isCustomPlanRefusal(quote)) {
    return {
      text: "Tu plan es personalizado: el paso a pagar con créditos, sin mensualidad, lo hacemos contigo.",
      button: "Pedirlo por WhatsApp",
      scheduled: false,
      whatsapp: true,
    };
  }
  if (quote.ok && quote.path === "immediate") {
    return {
      text: "Sin mensualidad ni débito automático: pagas cada conversación con tu saldo de créditos. Lo que debas del plan por uso pasa a tu saldo.",
      button: "Pagar con créditos",
      scheduled: false,
    };
  }
  if (quote.ok && quote.path === "scheduled") {
    return {
      text: `Sin mensualidad desde el cierre de tu ciclo, el ${longDate(quote.effectiveDate)}. Tu plan sigue igual hasta ese día.`,
      button: "Pagar con créditos",
      scheduled: false,
    };
  }
  if (quote.reason === "already_scheduled" && quote.scheduled) {
    return {
      text: `Tu cuenta pasa a pagar con créditos el ${longDate(quote.scheduled.effectiveDate)}.`,
      button: quote.scheduled.cancellable ? "Ver o cancelar el cambio" : "Ver el cambio",
      scheduled: true,
    };
  }
  if (quote.reason === "debt_pending") {
    return {
      text: "Puedes pasar a pagar con créditos cuando pagues lo pendiente de tu plan.",
      button: "Ver detalles",
      scheduled: false,
    };
  }
  return null;
}
